var _excluded = ["text", "name", "value", "disabled"],
  _excluded2 = ["id", "className", "isFluid", "label", "required", "helpText", "errorText", "warningText", "successText", "hasError", "hasWarning", "hasSuccess", "options", "leftAlignedLabel", "onFocus", "onBlur", "name", "onChange", "size", "utils", "type", "isInverse", "aria-label"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import Hint from '../hint';
import generateID from '../../utilities/generateID';
import Label from '../label';
import HorizontalList from '../horizontal-list';
import InverseContext from '../../utilities/inverse-context';
import StyledButtonGroup from './button-group.styles';
import StyledButtonGroupItem from './styles/button-group.item.styles';
import StyledButtonGroupItemContainer from './styles/button-group.item-container.styles';
import StyledButtonGroupItemInput from './styles/button-group.item-input.styles';
import StyledButtonGroupItemOutline from './styles/button-group.item-outline.styles';
import StyledButtonGroupItemText from './styles/button-group.item-text.styles';
import StyledButtonGroupFieldset from './styles/button-group.fieldset.styles';
import { StyledCacheWrapper } from '../../styles';
import withDeprecatedProps from '../../utilities/with-deprecated-props/withDeprecatedProps';
var buttonGroupPropTypes = {
  /** Array of items that will be rendered as buttons in a ButtonGroup */
  options: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string,
    value: PropTypes.string,
    defaultChecked: PropTypes.bool
  })),
  /** Label that will be displayed with a ButtonGroup */
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /** Adds a unique identifier */
  id: PropTypes.string,
  /** Adds custom CSS classes to ButtonGroup component */
  className: PropTypes.string,
  /** Toggles the error state */
  hasError: PropTypes.bool,
  /** Toggles the warning state */
  hasWarning: PropTypes.bool,
  /** Toggles the sucess state */
  hasSuccess: PropTypes.bool,
  /** Makes ButtonGroup span width of it's parent container */
  isFluid: PropTypes.bool,
  /** Aligns the label to the left of the ButtonGroup */
  leftAlignedLabel: PropTypes.bool,
  /** Text to be displayed for error state */
  errorText: PropTypes.node,
  /** Text to be displayed for warning state */
  warningText: PropTypes.node,
  /** Text to be displayed for success state  */
  successText: PropTypes.node,
  /** Unobtrusively styled text to appear below formField to aid user input. */
  helpText: PropTypes.node,
  /** Specifies whether the form field is required. */
  required: PropTypes.bool,
  /** Name assigned to checkbox or radio button */
  name: PropTypes.string,
  /** Callback to be triggered on blur */
  onBlur: PropTypes.func,
  /** Callback to be triggered on focus */
  onFocus: PropTypes.func,
  /** Callback to be triggered onChange of any of the inputs */
  onChange: PropTypes.func,
  /** Callback to be triggered onChange of any of the inputs */
  type: PropTypes.oneOf(['checkbox', 'radio']),
  /** the buttons size (t-shirt sizing) */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  /** Component design with dark background. */
  isInverse: PropTypes.oneOf(['auto', true, false]),
  /** Aria label for button */
  'aria-label': PropTypes.string,
  /**
   * @deprecated since 2.56.0
   * @see aria-label
   */
  ariaLabel: PropTypes.string,
  /** Add utility objects for styling */
  utils: PropTypes.object
};
var buttonGroupDefaultProps = {
  id: '',
  options: [],
  className: '',
  label: '',
  warningText: '',
  successText: '',
  name: 'formOption',
  hasError: false,
  hasWarning: false,
  hasSuccess: false,
  isFluid: false,
  leftAlignedLabel: false,
  helpText: '',
  required: false,
  errorText: '',
  size: 'md',
  isInverse: 'auto',
  type: 'radio',
  'aria-label': null,
  ariaLabel: null,
  utils: {},
  onBlur: function onBlur() {},
  onChange: function onChange() {},
  onFocus: function onFocus() {}
};
var createLegend = function createLegend(label, leftAlignedLabel, required) {
  if (label) {
    return /*#__PURE__*/React.createElement("legend", null, /*#__PURE__*/React.createElement(Label, {
      text: label,
      required: required
    }));
  }
};
var createHint = function createHint(errorText, useId) {
  return /*#__PURE__*/React.createElement(Hint, {
    id: "".concat(useId, "_error"),
    text: errorText,
    type: "error"
  });
};
var generateDescribedBy = function generateDescribedBy(hasError, hasWarning, hasSuccess, helpText, useId) {
  var optionStatusId = '';
  if (hasError) {
    optionStatusId = "".concat(useId, "_error");
  } else if (hasWarning) {
    optionStatusId = "".concat(useId, "_warning");
  } else if (hasSuccess) {
    optionStatusId = "".concat(useId, "_success");
  }
  var hintId = !hasError && !hasWarning && !hasSuccess && helpText ? "".concat(useId, "_hint") : '';
  return optionStatusId + ' ' + hintId;
};
var createOption = function createOption(_ref) {
  var option = _ref.option,
    optionId = _ref.optionId,
    isInverse = _ref.isInverse,
    onBlur = _ref.onBlur,
    onButtonChange = _ref.onButtonChange,
    onFocus = _ref.onFocus,
    name = _ref.name,
    required = _ref.required,
    type = _ref.type,
    ariaLabel = _ref.ariaLabel,
    ariaDescribedBy = _ref.ariaDescribedBy;
  var text = option.text,
    optionName = option.name,
    value = option.value,
    disabled = option.disabled,
    other = _objectWithoutProperties(option, _excluded);
  return /*#__PURE__*/React.createElement(StyledCacheWrapper, {
    key: optionId
  }, /*#__PURE__*/React.createElement(StyledButtonGroupItem, {
    key: optionId,
    isInverse: isInverse,
    htmlFor: other.id || optionId
  }, /*#__PURE__*/React.createElement(StyledButtonGroupItemContainer, null, /*#__PURE__*/React.createElement(StyledButtonGroupItemInput, _extends({
    id: optionId,
    isInverse: isInverse,
    key: optionId,
    disabled: disabled,
    type: type,
    name: optionName || name,
    value: value,
    onFocus: onFocus,
    onBlur: onBlur,
    required: required,
    onChange: onButtonChange,
    "aria-label": ariaLabel,
    "aria-describedby": ariaDescribedBy,
    className: "notranslate"
  }, other)), /*#__PURE__*/React.createElement(StyledButtonGroupItemText, null, text), /*#__PURE__*/React.createElement(StyledButtonGroupItemOutline, null))));
};

/**
 * @ignore @description
 * ## ButtonGroup Component
 * The Button Group can be implemented in multiple ways using different components based on what functionality you require.
 *
 * @ignore @see Mesh: [Button Group Docs](https://mesh-component-library.apps.csts-k8cnp-ro-1.r-caas.allstate.com/?path=/story/forms-buttongroup--default)
 *
 * @ignore @example
 * ```jsx
 * import { ButtonGroup } from 'mesh-component-library';
 *
 * <ButtonGroup {...props} />
 * ```
 */
export var ButtonGroup = function ButtonGroup(props) {
  var id = props.id,
    className = props.className,
    isFluid = props.isFluid,
    label = props.label,
    required = props.required,
    helpText = props.helpText,
    errorText = props.errorText,
    warningText = props.warningText,
    successText = props.successText,
    hasError = props.hasError,
    hasWarning = props.hasWarning,
    hasSuccess = props.hasSuccess,
    options = props.options,
    leftAlignedLabel = props.leftAlignedLabel,
    onFocus = props.onFocus,
    onBlur = props.onBlur,
    name = props.name,
    onChange = props.onChange,
    size = props.size,
    utils = props.utils,
    type = props.type,
    isInverse = props.isInverse,
    ariaLabel = props['aria-label'],
    other = _objectWithoutProperties(props, _excluded2);
  var useId = id || useRef(generateID('button-group-')).current;
  var buttonGroup = useRef();
  var onButtonChange = function onButtonChange(e) {
    onChange(e);
  };
  var useIsInverse = React.useContext(InverseContext);
  if (isInverse !== 'auto') {
    useIsInverse = isInverse;
  }
  var renderHints = function renderHints() {
    return /*#__PURE__*/React.createElement(React.Fragment, null, successText || errorText && hasError || warningText && hasWarning ? null : /*#__PURE__*/React.createElement(React.Fragment, null, helpText && /*#__PURE__*/React.createElement(Hint, {
      id: "".concat(useId, "_hint"),
      text: helpText
    })), hasError ? createHint(errorText, useId) : null, hasWarning ? /*#__PURE__*/React.createElement(Hint, {
      type: "warning",
      id: "".concat(useId, "_warning"),
      text: warningText
    }) : '', successText ? /*#__PURE__*/React.createElement(Hint, {
      type: "success",
      id: "".concat(useId, "_success"),
      text: successText
    }) : null);
  };
  var btnGroup = /*#__PURE__*/React.createElement(StyledButtonGroup, {
    ref: buttonGroup,
    className: className,
    role: "group",
    size: size,
    isFluid: isFluid,
    hasError: hasError,
    hasWarning: hasWarning,
    hasSuccess: hasSuccess,
    isInverse: useIsInverse
  }, options.map(function (option, i) {
    return createOption({
      option: option,
      optionId: "".concat(useId, "-control-item-").concat(i),
      isInverse: useIsInverse,
      onBlur: onBlur,
      onButtonChange: onButtonChange,
      onFocus: onFocus,
      name: name,
      required: required,
      type: type,
      ariaLabel: ariaLabel,
      ariaDescribedBy: "".concat(generateDescribedBy(hasError, hasWarning, hasSuccess, helpText, useId))
    });
  }));
  return /*#__PURE__*/React.createElement(StyledButtonGroupFieldset, _extends({}, other, {
    isInverse: useIsInverse,
    utils: utils
  }), leftAlignedLabel ? /*#__PURE__*/React.createElement(HorizontalList, {
    align: "middle",
    flex: true,
    items: [{
      item: /*#__PURE__*/React.createElement("div", null, createLegend(label, leftAlignedLabel, required))
    }, {
      item: /*#__PURE__*/React.createElement("div", null, btnGroup)
    }]
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, createLegend(label, leftAlignedLabel, required), btnGroup), renderHints());
};
ButtonGroup.propTypes = buttonGroupPropTypes;
ButtonGroup.defaultProps = buttonGroupDefaultProps;
ButtonGroup.displayName = 'ButtonGroup';
/**
 * @type React.FC<buttonGroupPropTypes>
 */
export default withDeprecatedProps(ButtonGroup);